import React, { useState } from "react";
import "./Antivirus.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faLaptop,
  faBuilding,
  faWifi,
  faUserShield,
  faCloud,
} from "@fortawesome/free-solid-svg-icons";
import ServiceContactModal from "../ServiceContactModal/ServiceContactModal"; // adjust path if needed

const avServices = [
  {
    title: "Home & Family Protection",
    icon: faLaptop,
    tag: "Home",
    desc: "Setup, optimisation and ongoing protection for laptops, desktops and mobile devices used at home.",
  },
  {
    title: "Small Business Endpoint Security",
    icon: faBuilding,
    tag: "Business",
    desc: "Managed antivirus for small US offices, remote teams and hybrid environments – fully monitored and updated.",
  },
  {
    title: "Multi-Device & Wi-Fi Security",
    icon: faWifi,
    tag: "Network",
    desc: "Protect all devices connected to your home or office network, including guest devices and IoT hardware.",
  },
  {
    title: "Ransomware & Phishing Protection",
    icon: faUserShield,
    tag: "Threats",
    desc: "Advanced protection and response plans focused on common US-targeted threats like ransomware and phishing.",
  },
  {
    title: "Cloud & Remote Worker Security",
    icon: faCloud,
    tag: "Remote",
    desc: "Policies, VPNs and antivirus rollout for fully remote or hybrid teams working from anywhere in the US.",
  },
  {
    title: "Virus Cleanup & Migration Help",
    icon: faShieldAlt,
    tag: "Rescue",
    desc: "Removal of existing malware, then migration to trusted antivirus plans with hardened security settings.",
  },
];

const avPlans = [
  {
    name: "Essential Protection",
    price: "$8.99/mo",
    devices: "Up to 3 devices",
    bestFor: "Solo users & small families",
    features: [
      "Real-time antivirus & malware protection",
      "Safe browsing & basic phishing filters",
      "Annual security health check",
    ],
    recommended: false,
  },
  {
    name: "Advanced Family & Home Office",
    price: "$14.99/mo",
    devices: "Up to 10 devices",
    bestFor: "Families, freelancers & home offices",
    features: [
      "Everything in Essential",
      "Multi-device protection (PC, Mac, mobile)",
      "Wi-Fi & router security hardening",
      "US-based remote setup & support",
    ],
    recommended: true,
  },
  {
    name: "Business Endpoint Suite",
    price: "Custom",
    devices: "10+ devices",
    bestFor: "US small & mid-size businesses",
    features: [
      "Centralised endpoint management",
      "Policies by department / role",
      "Patch management & reporting",
      "Staff security awareness guidance",
    ],
    recommended: false,
  },
];

const brands = [
  "McAfee",
  "Norton",
  "Kaspersky",
  "TotalAV",
  "AVG",
  "Bitdefender",
];

const Antivirus = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(
    "Antivirus & Endpoint Security"
  );

  const openModal = (serviceLabel = "Antivirus & Endpoint Security") => {
    setSelectedService(serviceLabel);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <section className="av-section">
        <div className="av-inner">
          {/* Hero / intro */}
          <header className="av-header">
            <p className="av-eyebrow">ANTIVIRUS & ENDPOINT SECURITY</p>
            <h1 className="av-title">
              Antivirus Services &amp; Plans for US Homes and Businesses
            </h1>
            <p className="av-sub">
              We help US families and companies choose, configure and manage the
              right antivirus stack — across McAfee, Norton, Kaspersky, TotalAV,
              AVG, Bitdefender and more — so every device stays protected and
              compliant.
            </p>

            <div className="av-pill-row">
              <span className="av-pill">US-focused security setup</span>
              <span className="av-pill">Home &amp; business coverage</span>
              <span className="av-pill">Multi-brand support</span>
            </div>

            <div className="av-cta-row">
              <button
                type="button"
                className="av-cta-primary"
                onClick={() => openModal("Antivirus & Endpoint Security")}
              >
                Get a recommended antivirus plan
              </button>
              <p className="av-cta-note">
                Tell us how many devices you have and how you use them — we’ll
                match a plan and setup flow in plain English.
              </p>
            </div>
          </header>

          {/* Brand row */}
          <section className="av-block">
            <div className="av-block-header">
              <h2 className="av-section-title">Brands we deploy and manage</h2>
              <p className="av-section-sub">
                We work with leading antivirus brands and tailor them to your
                devices, budget and compliance needs — instead of pushing a
                single vendor.
              </p>
            </div>

            <div className="av-brand-row">
              {brands.map((brand, idx) => (
                <span key={idx} className="av-brand-pill">
                  <FontAwesomeIcon
                    icon={faShieldAlt}
                    className="av-brand-icon"
                  />
                  {brand}
                </span>
              ))}
            </div>
          </section>

          {/* Services grid */}
          <section className="av-block">
            <div className="av-block-header">
              <h2 className="av-section-title">Antivirus services in the US</h2>
              <p className="av-section-sub">
                From one-time cleanups to fully managed endpoint protection, we
                can handle the setup, monitoring and renewal work so you don’t
                have to.
              </p>
            </div>

            <div className="av-grid">
              {avServices.map((service, idx) => (
                <article key={idx} className="av-card">
                  <div className="av-card-top">
                    <div className="av-icon">
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                    <span className="av-tag">{service.tag}</span>
                  </div>
                  <h3 className="av-card-title">{service.title}</h3>
                  <p className="av-card-desc">{service.desc}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Plans grid */}
          <section className="av-block">
            <div className="av-block-header">
              <h2 className="av-section-title">Antivirus plans &amp; coverage</h2>
              <p className="av-section-sub">
                Not sure where to start? These example plans show how we
                typically package protection for US homes, home offices and
                growing businesses.
              </p>
            </div>

            <div className="av-plans-grid">
              {avPlans.map((plan, idx) => (
                <article
                  key={idx}
                  className={`av-plan-card ${
                    plan.recommended ? "av-plan-card--featured" : ""
                  }`}
                >
                  {plan.recommended && (
                    <span className="av-plan-badge">Most popular</span>
                  )}
                  <h3 className="av-plan-name">{plan.name}</h3>
                  <p className="av-plan-price">{plan.price}</p>
                  <p className="av-plan-devices">{plan.devices}</p>
                  <p className="av-plan-bestfor">{plan.bestFor}</p>

                  <ul className="av-plan-list">
                    {plan.features.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className="av-plan-btn"
                    onClick={() =>
                      openModal(`Antivirus – ${plan.name}`)
                    }
                  >
                    Talk about this plan
                  </button>
                </article>
              ))}
            </div>
          </section>

          {/* Bottom note */}
          <section className="av-bottom-note">
            <div className="av-bottom-note-inner">
              <p>
                Need to migrate from an existing antivirus or secure a new US
                office? We can audit what you have, clean up old devices and
                roll out a clear, managed plan across your team.
              </p>
            </div>
          </section>
        </div>
      </section>

      {/* Shared contact modal */}
      <ServiceContactModal
        isOpen={isModalOpen}
        onClose={closeModal}
        defaultService={selectedService}
      />
    </>
  );
};

export default Antivirus;
